import { localized, msg, str } from '@lit/localize';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@spectrum-web-components/button/sp-button.js';
import '@spectrum-web-components/menu/sp-menu-item.js';
import '@spectrum-web-components/overlay/overlay-trigger.js';
import '@spectrum-web-components/picker/sp-picker.js';
import '@spectrum-web-components/table/sp-table.js';
import '@spectrum-web-components/table/sp-table-body.js';
import '@spectrum-web-components/table/sp-table-cell.js';
import '@spectrum-web-components/table/sp-table-head.js';
import '@spectrum-web-components/table/sp-table-head-cell.js';
import '@spectrum-web-components/table/sp-table-row.js';
import '@spectrum-web-components/textfield/sp-textfield.js';
import '@spectrum-web-components/tooltip/sp-tooltip.js';

import '../../components/confirmation-dialog';
import { api } from '../../api';
import type {
  FeatureFlagDefinition,
  FeatureFlagKey,
  FeatureFlags,
  FeatureFlagsAdminMetadata,
} from '../../api/types';
import { DEFAULT_FEATURE_FLAGS } from '../../config/feature-flags';
import { sharedUiStyles } from '../../styles/shared-ui.styles';
import { getUnassignedAdminUsers } from './admin-user-classification';

type AdminUser = {
  id: string;
  name?: string | null;
  email?: string | null;
  passwordAuthEnabled?: boolean;
  emailVerifiedAt?: string | null;
  activeOrganizationIds?: string[];
};

type AdminOrganizationSummary = {
  id: string;
  name: string;
  description?: string | null;
};

type PendingAdminConfirmation =
  | { type: 'remove-user'; orgId: string; userId: string }
  | { type: 'disable-org-feature'; orgId: string; key: FeatureFlagKey };

@customElement('kv-module-admin')
@localized()
export class KvModuleAdmin extends LitElement {
  static styles = [
    sharedUiStyles,
    css`
    :host {
      display: block;
      padding: 28px;
      color: var(--color-app-text, #1f1f1f);
      background:
        linear-gradient(
          180deg,
          var(--color-sidebar-bg, #f8fafc) 0%,
          var(--color-app-bg, #f8fafc) 100%
        );
      min-height: 100%;
      height: 100%;
      overflow: auto;
      box-sizing: border-box;
      --admin-surface: var(--kv-surface);
      --admin-surface-muted: var(--kv-surface-muted);
      --admin-surface-hover: var(--kv-surface-hover);
      --admin-border: var(--kv-border);
      --admin-accent: var(--kv-accent);
      --admin-accent-bg: var(--kv-accent-bg);
      --admin-control-bg: var(--kv-control-bg);
      --admin-control-hover: var(--kv-control-hover);
      --admin-control-active: var(--kv-control-active);
      --admin-control-border: var(--kv-control-border);
      --admin-danger: var(--kv-danger);
      --admin-danger-bg: var(--kv-danger-bg);
      --admin-danger-hover: var(--kv-danger-hover);
      --admin-danger-active: var(--kv-danger-active);
      --admin-danger-border: var(--kv-danger-border);
      --admin-warning-bg: var(--kv-warning-bg);
      --admin-warning-text: var(--kv-warning-text);
      --admin-shadow: var(--kv-shadow);
    }

    :host-context(sp-theme[color='dark']) {
      --admin-shadow: none;
    }

    .admin-shell {
      display: flex;
      flex-direction: column;
      gap: 18px;
      margin: 0 auto;
      max-width: 1280px;
      min-width: 0;
      width: min(100%, 1280px);
    }

    .admin-header {
      display: flex;
      flex-direction: column;
      gap: 6px;
      max-width: 760px;
    }

    .admin-title {
      color: var(--color-app-text, #1f1f1f);
      font-size: 24px;
      font-weight: 750;
      line-height: 1.15;
      margin: 0;
    }

    .admin-description {
      color: var(--color-app-text-secondary, #6b7280);
      font-size: 13px;
      line-height: 1.45;
      margin: 0;
    }

    .admin-top-grid {
      align-items: start;
      display: grid;
      gap: 14px;
      grid-template-columns: minmax(340px, 0.8fr) minmax(0, 1.2fr);
    }

    .panel,
    .org-card,
    .state-panel {
      background: var(--admin-surface);
      border: 1px solid var(--admin-border);
      border-radius: var(--kv-radius-panel);
      box-shadow: var(--admin-shadow);
      box-sizing: border-box;
      min-width: 0;
    }

    .panel,
    .state-panel {
      padding: 16px;
    }

    .panel-header,
    .org-card-header {
      display: flex;
      gap: 14px;
      justify-content: space-between;
      min-width: 0;
    }

    .panel-header {
      align-items: flex-start;
      margin-bottom: 14px;
    }

    .org-card-header {
      align-items: center;
      border-bottom: 1px solid var(--admin-border);
      padding: 16px;
      flex-wrap: wrap;
    }

    .panel-title,
    .org-title {
      color: var(--color-app-text, #1f1f1f);
      font-size: 14px;
      font-weight: 700;
      line-height: 1.25;
      margin: 0;
    }

    .panel-description,
    .org-description,
    .status {
      color: var(--color-app-text-secondary, #6b7280);
      font-size: 12px;
      line-height: 1.4;
      margin: 4px 0 0;
    }

    .org-copy {
      display: flex;
      flex-direction: column;
      min-width: 0;
    }

    .org-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    .create-form {
      align-items: end;
      display: grid;
      gap: 10px;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
    }

    label {
      color: var(--color-app-text, #1f1f1f);
      display: grid;
      font-size: 12px;
      font-weight: 650;
      gap: 6px;
      min-width: 0;
    }

    .table-shell {
      background: var(--admin-surface);
      border: 1px solid var(--admin-border);
      border-radius: var(--kv-radius-panel);
      overflow: auto;
    }

    .unassigned-table,
    .members-table {
      min-width: 760px;
      width: 100%;
    }

    .members-table {
      min-width: 1040px;
    }

    .org-list {
      display: flex;
      flex-direction: column;
      gap: 14px;
    }

    .org-card-body {
      display: grid;
      gap: 14px;
      padding: 14px 16px 16px;
    }

    .feature-bulk-actions {
      display: grid;
      gap: 8px;
      grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    }

    .feature-action-row {
      align-items: center;
      background: transparent;
      border: 0;
      border-top: 1px solid var(--admin-border);
      border-radius: 0;
      display: grid;
      gap: 10px;
      grid-template-columns: minmax(82px, 1fr) auto;
      justify-content: space-between;
      min-width: 0;
      padding: 9px 0;
    }

    .feature-action-row:first-child {
      border-top: none;
      padding-top: 0;
    }

    .feature-action-label {
      color: var(--color-app-text, #1f1f1f);
      font-size: 12px;
      font-weight: 700;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .feature-action-buttons {
      display: flex;
      flex: 0 0 auto;
      gap: 6px;
      justify-content: flex-end;
    }

    .status-pill {
      align-items: center;
      background: var(--admin-surface-muted);
      border: 1px solid var(--admin-border);
      border-radius: var(--kv-radius-chip);
      color: var(--color-app-text-secondary, #6b7280);
      display: inline-flex;
      font-size: 12px;
      font-weight: 700;
      gap: 5px;
      line-height: 1.2;
      padding: 4px 9px;
      white-space: nowrap;
    }

    .status-pill.success {
      background: var(--color-success-bg, #efe);
      border-color: transparent;
      color: var(--color-success-text, #15803d);
    }

    .status-pill.warning {
      background: var(--admin-warning-bg);
      border-color: transparent;
      color: var(--admin-warning-text);
    }

    .status-pill.accent {
      background: var(--admin-accent-bg);
      border-color: var(--kv-accent-border-subtle);
      color: var(--admin-accent);
    }

    .cell-muted {
      color: var(--color-app-text-secondary, #6b7280);
    }

    .user-removed {
      color: var(--color-app-text-secondary, #6b7280);
      font-style: italic;
    }

    .state-panel {
      align-items: center;
      border-style: dashed;
      background: transparent;
      color: var(--color-app-text-secondary, #6b7280);
      display: flex;
      justify-content: center;
      min-height: 132px;
      text-align: center;
    }

    .error {
      background: var(--color-error-bg, #fee);
      border-radius: 8px;
      color: var(--color-error-text, #b91c1c);
      font-size: 13px;
      line-height: 1.35;
      margin: 0;
      padding: 12px 14px;
    }

    sp-textfield,
    sp-picker {
      width: 100%;
    }

    sp-textfield {
      --mod-textfield-background-color: var(--admin-surface);
      --spectrum-textfield-background-color: var(--admin-surface);
      --mod-textfield-border-color: var(--admin-border);
      --mod-textfield-focus-indicator-color: var(--admin-accent);
    }

    sp-picker {
      --mod-picker-background-color-default: var(--admin-control-bg);
      --mod-picker-background-color-default-open: var(--admin-control-bg);
      --mod-picker-background-color-hover: var(--admin-control-hover);
      --mod-picker-background-color-hover-open: var(--admin-control-hover);
      --mod-picker-background-color-active: var(--admin-control-active);
      --mod-picker-background-color-key-focus: var(--admin-control-bg);
      --mod-picker-border-color-default: var(--admin-control-border);
      --mod-picker-border-default-open: var(--kv-accent-border);
      --mod-picker-border-color-default-open: var(--kv-accent-border);
      --mod-picker-border-color-hover: var(--kv-accent-border);
      --mod-picker-border-color-hover-open: var(--kv-accent-border);
      --mod-picker-border-active: var(--color-accent-border-strong);
      --mod-picker-border-color-active: var(--color-accent-border-strong);
      --mod-picker-border-color-key-focus: var(--admin-accent);
      --mod-picker-focus-indicator-color: var(--admin-accent);
      --mod-picker-font-color-default: var(--color-app-text, #1f1f1f);
      --mod-picker-font-color-hover: var(--color-app-text, #1f1f1f);
      --mod-picker-icon-color-default: var(--admin-accent);
      --mod-picker-icon-color-hover: var(--admin-accent);
    }

    sp-button[variant='secondary']:not(.admin-danger-button):not(
        .flag-toggle-disabled
      ) {
      --mod-button-background-color-default: var(--admin-control-bg);
      --mod-button-background-color-hover: var(--admin-control-hover);
      --mod-button-background-color-down: var(--admin-control-active);
      --mod-button-background-color-focus: var(--admin-control-bg);
      --mod-button-border-color-default: var(--admin-control-border);
      --mod-button-border-color-hover: var(--kv-accent-border);
      --mod-button-border-color-down: var(--color-accent-border-strong);
      --mod-button-border-color-focus: var(--admin-accent);
      --mod-button-content-color-default: var(--admin-accent);
      --mod-button-content-color-hover: var(--admin-accent);
      --mod-button-content-color-down: var(--admin-accent);
      --mod-button-content-color-focus: var(--admin-accent);
      --mod-button-focus-ring-color: var(--admin-accent);
    }

    sp-button.admin-danger-button {
      --mod-button-background-color-default: var(--admin-danger-bg);
      --mod-button-background-color-hover: var(--admin-danger-hover);
      --mod-button-background-color-down: var(--admin-danger-active);
      --mod-button-background-color-focus: var(--admin-danger-bg);
      --mod-button-border-color-default: var(--admin-danger-border);
      --mod-button-border-color-hover: color-mix(
        in srgb,
        var(--kv-danger-border) 70%,
        var(--kv-danger)
      );
      --mod-button-border-color-down: color-mix(
        in srgb,
        var(--kv-danger-border) 55%,
        var(--kv-danger)
      );
      --mod-button-border-color-focus: var(--admin-danger);
      --mod-button-content-color-default: var(--admin-danger);
      --mod-button-content-color-hover: var(--admin-danger);
      --mod-button-content-color-down: var(--admin-danger);
      --mod-button-content-color-focus: var(--admin-danger);
      --mod-button-focus-ring-color: var(--admin-danger);
    }

    sp-button.flag-toggle-disabled {
      --mod-button-background-color-default: var(--admin-surface-muted);
      --mod-button-background-color-hover: var(--admin-surface-hover);
      --mod-button-border-color-default: var(--admin-border);
      --mod-button-content-color-default: var(--color-app-text-secondary);
      --mod-button-content-color-hover: var(--color-app-text);
    }

    .col-email {
      width: 240px;
      min-width: 240px;
      max-width: 240px;
      box-sizing: border-box;
    }

    .email-cell {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      cursor: default;
    }

    .removed-label {
      margin-left: 6px;
    }

    @media (max-width: 1080px) {
      .admin-top-grid,
      .create-form {
        grid-template-columns: 1fr;
      }
    }

    @media (max-width: 760px) {
      :host {
        padding: 16px 12px;
      }

      .admin-header {
        padding-left: var(--kv-mobile-title-inline-start);
      }

      .admin-title {
        font-size: 22px;
      }

      .panel,
      .state-panel,
      .org-card-header,
      .org-card-body {
        padding: 14px;
      }
    }
    `,
  ];

  @state() accessor metadata: FeatureFlagsAdminMetadata | null = null;
  @state() accessor loading = false;
  @state() accessor errorMessage: string | null = null;
  @state() accessor updatingUserId: string | null = null;
  @state() accessor removingUserId: string | null = null;
  @state() accessor restoringUserId: string | null = null;
  @state() accessor assigningUserId: string | null = null;
  @state() accessor creatingOrganization = false;
  @state() accessor newOrganizationName = '';
  @state() accessor newOrganizationDescription = '';
  @state() accessor selectedOrgByUser: Record<string, string> = {};
  @state() accessor pendingConfirmation: PendingAdminConfirmation | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    void this.loadMetadata();
  }

  private get defaults(): FeatureFlags {
    return this.metadata?.defaults ?? { ...DEFAULT_FEATURE_FLAGS };
  }

  private get featureFlags(): FeatureFlagDefinition[] {
    return this.metadata?.featureFlags ?? [];
  }

  private get orgs(): AdminOrganizationSummary[] {
    return this.metadata?.organizations ?? [];
  }

  private get users(): AdminUser[] {
    return this.metadata?.users ?? [];
  }

  private get userFlags(): Array<{
    userId: string;
    key: FeatureFlagKey;
    enabled: boolean;
  }> {
    return this.metadata?.userFlags ?? [];
  }

  private get memberships(): Array<{
    userId: string;
    organizationId: string;
    removedAt: string | null;
  }> {
    return this.metadata?.memberships ?? [];
  }

  private formatFeatureFlagLabel(flag: FeatureFlagDefinition): string {
    switch (flag.key) {
      case 'chat':
        return msg('Chat');
      case 'workflows':
        return msg('Workflows');
      case 'remoteapp':
        return msg('Remote App');
      case 'audit':
        return msg('Audit');
      case 'agent':
        return msg('Agent');
      case 'settings':
        return msg('Settings');
      case 'usage':
        return msg('Usage');
    }
  }

  private async loadMetadata() {
    this.loading = true;
    this.errorMessage = null;
    try {
      const metadata = await api.admin.getAdminMetadata();
      this.metadata = metadata;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.errorMessage = message || msg('Failed to load admin metadata.');
    } finally {
      this.loading = false;
    }
  }

  private getCountsForOrg(orgId: string): {
    activeCount: number;
    totalCount: number;
  } {
    const row = this.metadata?.organizationUserCounts?.find(
      (c) => c.organizationId === orgId
    );
    return row
      ? { activeCount: row.activeCount, totalCount: row.totalCount }
      : { activeCount: 0, totalCount: 0 };
  }

  private requestRemoveUserFromOrg(orgId: string, userId: string) {
    this.pendingConfirmation = { type: 'remove-user', orgId, userId };
  }

  private async removeUserFromOrg(orgId: string, userId: string) {
    this.errorMessage = null;
    this.removingUserId = userId;
    try {
      await api.admin.removeUserFromOrganization(orgId, userId);
      await this.loadMetadata();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.errorMessage = message || msg('Failed to remove user.');
    } finally {
      this.removingUserId = null;
    }
  }

  private requestDisableUserFlag(userId: string, key: FeatureFlagKey) {
    void this.toggleUserFlag(userId, key, false);
  }

  private requestDisableOrgFeature(orgId: string, key: FeatureFlagKey) {
    this.pendingConfirmation = { type: 'disable-org-feature', orgId, key };
  }

  private cancelPendingConfirmation = () => {
    this.pendingConfirmation = null;
  };

  private confirmPendingConfirmation = async () => {
    const pending = this.pendingConfirmation;
    if (!pending) return;
    this.pendingConfirmation = null;
    switch (pending.type) {
      case 'remove-user':
        await this.removeUserFromOrg(pending.orgId, pending.userId);
        break;
      case 'disable-org-feature':
        await this.toggleOrgFeature(pending.orgId, pending.key, false);
        break;
    }
  };

  private async restoreUserToOrg(orgId: string, userId: string) {
    this.errorMessage = null;
    this.restoringUserId = userId;
    try {
      await api.admin.restoreUserToOrganization(orgId, userId);
      await this.loadMetadata();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.errorMessage = message || msg('Failed to restore user.');
    } finally {
      this.restoringUserId = null;
    }
  }

  private async createOrganization(event: Event) {
    event.preventDefault();
    if (this.creatingOrganization) return;

    const name = this.newOrganizationName.trim();
    if (!name) {
      this.errorMessage = msg('Organization name is required.');
      return;
    }

    this.creatingOrganization = true;
    this.errorMessage = null;
    try {
      await api.admin.createOrganization({
        name,
        description: this.newOrganizationDescription || undefined,
      });
      this.newOrganizationName = '';
      this.newOrganizationDescription = '';
      await this.loadMetadata();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.errorMessage = message || msg('Failed to create organization.');
    } finally {
      this.creatingOrganization = false;
    }
  }

  private async assignUserToSelectedOrg(userId: string) {
    const orgId = this.selectedOrgByUser[userId] || this.orgs[0]?.id;
    if (!orgId) {
      this.errorMessage = msg('Create an organization before assigning users.');
      return;
    }

    this.assigningUserId = userId;
    this.errorMessage = null;
    try {
      await api.admin.assignUserToOrganization(orgId, userId);
      await this.loadMetadata();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.errorMessage = message || msg('Failed to assign user.');
    } finally {
      this.assigningUserId = null;
    }
  }

  private selectOrgForUser(userId: string, event: Event) {
    const value = (event.currentTarget as { value?: string }).value ?? '';
    this.selectedOrgByUser = {
      ...this.selectedOrgByUser,
      [userId]: value,
    };
  }

  private computeUserEffective(userId: string | null): FeatureFlags {
    const base = { ...this.defaults };
    if (!userId) return base;
    for (const flag of this.userFlags) {
      if (flag.userId === userId) {
        base[flag.key] = flag.enabled;
      }
    }
    return base;
  }

  private async toggleUserFlag(
    userId: string,
    key: FeatureFlagKey,
    enabled: boolean
  ) {
    this.loading = true;
    this.updatingUserId = userId;
    this.errorMessage = null;
    try {
      const current = this.computeUserEffective(userId);
      const next = { ...current, [key]: enabled };
      await api.featureFlags.updateUserFlags(userId, next);
      await this.loadMetadata();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.errorMessage = message || msg('Failed to update user flag.');
      this.loading = false;
    } finally {
      this.updatingUserId = null;
    }
  }

  private async toggleOrgFeature(
    orgId: string,
    key: FeatureFlagKey,
    enabled: boolean
  ) {
    this.loading = true;
    this.errorMessage = null;
    try {
      await api.featureFlags.toggleOrgFeature(orgId, key, enabled);
      await this.loadMetadata();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.errorMessage =
        message || msg('Failed to update organization flags.');
      this.loading = false;
    }
  }

  /** Returns users in this org, with removed flag. Active users first, then removed. */
  private getUsersForOrg(orgId: string): Array<{
    user: AdminUser;
    removed: boolean;
  }> {
    const orgMemberships = this.memberships.filter(
      (m) => m.organizationId === orgId
    );
    const result: Array<{ user: AdminUser; removed: boolean }> = [];
    for (const m of orgMemberships) {
      const user = this.users.find((u) => u.id === m.userId);
      if (user)
        result.push({
          user,
          removed: m.removedAt != null && m.removedAt !== '',
        });
    }
    return result;
  }

  private get unassignedUsers(): AdminUser[] {
    return getUnassignedAdminUsers(this.users, this.memberships);
  }

  private canAssignUser(user: AdminUser): boolean {
    return !user.passwordAuthEnabled || Boolean(user.emailVerifiedAt);
  }

  private getUserAuthStatus(user: AdminUser): string {
    if (!user.passwordAuthEnabled) {
      return 'OAuth';
    }
    return user.emailVerifiedAt ? msg('Verified') : msg('Unverified');
  }

  private getUserAuthStatusTone(user: AdminUser): string {
    if (user.passwordAuthEnabled && user.emailVerifiedAt) return 'success';
    if (user.passwordAuthEnabled && !user.emailVerifiedAt) return 'warning';
    return 'neutral';
  }

  private getUserLabel(userId: string): string {
    const user = this.users.find((item) => item.id === userId);
    return user?.email || user?.name || userId;
  }

  private getOrgLabel(orgId: string): string {
    return this.orgs.find((org) => org.id === orgId)?.name ?? orgId;
  }

  private formatFeatureFlagKeyLabel(key: FeatureFlagKey): string {
    const flag = this.featureFlags.find((item) => item.key === key);
    if (flag) return this.formatFeatureFlagLabel(flag);
    switch (key) {
      case 'chat':
        return msg('Chat');
      case 'workflows':
        return msg('Workflows');
      case 'remoteapp':
        return msg('Remote App');
      case 'audit':
        return msg('Audit');
      case 'agent':
        return msg('Agent');
      case 'settings':
        return msg('Settings');
      case 'usage':
        return msg('Usage');
    }
  }

  private getUserFlagValue(userId: string, key: FeatureFlagKey): boolean {
    const effective = this.computeUserEffective(userId);
    return Boolean(effective[key]);
  }

  private renderPendingConfirmation() {
    const pending = this.pendingConfirmation;
    if (!pending) return nothing;

    if (pending.type === 'remove-user') {
      const userLabel = this.getUserLabel(pending.userId);
      const orgLabel = this.getOrgLabel(pending.orgId);
      return html`
        <kv-confirmation-dialog
          heading=${msg('Remove user from organization')}
          confirm-label=${msg('Remove')}
          .confirming=${this.removingUserId === pending.userId}
          @cancel=${this.cancelPendingConfirmation}
          @confirm=${this.confirmPendingConfirmation}
        >
          <p>
            ${msg(
              str`Remove "${userLabel}" from "${orgLabel}"? They will lose access to this organization until restored.`
            )}
          </p>
        </kv-confirmation-dialog>
      `;
    }

    const flagLabel = this.formatFeatureFlagKeyLabel(pending.key);
    const orgLabel = this.getOrgLabel(pending.orgId);
    return html`
      <kv-confirmation-dialog
        heading=${msg('Disable organization feature')}
        confirm-label=${msg('Disable')}
        @cancel=${this.cancelPendingConfirmation}
        @confirm=${this.confirmPendingConfirmation}
      >
        <p>
          ${msg(
            str`Disable ${flagLabel} for everyone in "${orgLabel}"? Active users in this organization will lose access to this module.`
          )}
        </p>
      </kv-confirmation-dialog>
    `;
  }

  private renderPage(content: unknown) {
    return html`
      <div class="admin-shell">
        <header class="admin-header">
          <h1 class="admin-title">${msg('Admin')}</h1>
          <p class="admin-description">
            ${msg('Manage organizations, users, and module access.')}
          </p>
        </header>
        ${content}
      </div>
      ${this.renderPendingConfirmation()}
    `;
  }

  private renderUserEmail(user: AdminUser, removed = false) {
    const emailLabel = user.email || user.name || user.id;
    if (removed) {
      return html`
        <span class="email-cell">${emailLabel}</span>
        <span class="cell-muted removed-label">${msg('(removed)')}</span>
      `;
    }
    return html`
      <overlay-trigger placement="top" type="hint" triggered-by="hover">
        <span class="email-cell" slot="trigger">${emailLabel}</span>
        <sp-tooltip slot="hover-content">${emailLabel}</sp-tooltip>
      </overlay-trigger>
    `;
  }

  private renderUserStatus(user: AdminUser) {
    return html`
      <span class="status-pill ${this.getUserAuthStatusTone(user)}">
        ${this.getUserAuthStatus(user)}
      </span>
    `;
  }

  private renderCreateOrganizationPanel() {
    return html`
      <section class="panel">
        <div class="panel-header">
          <div>
            <h2 class="panel-title">${msg('New organization')}</h2>
            <p class="panel-description">
              ${msg('Create organizations before assigning password users.')}
            </p>
          </div>
        </div>
        <form class="create-form" @submit=${this.createOrganization}>
          <label>
            ${msg('Name')}
            <sp-textfield
              .value=${this.newOrganizationName}
              @input=${(event: Event) =>
                (this.newOrganizationName = inputValue(event))}
            ></sp-textfield>
          </label>
          <label>
            ${msg('Description')}
            <sp-textfield
              .value=${this.newOrganizationDescription}
              @input=${(event: Event) =>
                (this.newOrganizationDescription = inputValue(event))}
            ></sp-textfield>
          </label>
          <sp-button
            type="submit"
            variant="accent"
            ?disabled=${this.creatingOrganization}
          >
            ${msg('Create')}
          </sp-button>
        </form>
      </section>
    `;
  }

  private renderUnassignedUsersPanel() {
    return html`
      <section class="panel">
        <div class="panel-header">
          <div>
            <h2 class="panel-title">${msg('Unassigned users')}</h2>
            <p class="panel-description">
              ${msg('Assign verified password users or OAuth users to an organization.')}
            </p>
          </div>
          <span class="status-pill accent">${this.unassignedUsers.length}</span>
        </div>
        ${
          this.unassignedUsers.length === 0
            ? html`<div class="state-panel">${msg('No unassigned users.')}</div>`
            : html`
                <div class="table-shell">
                  <sp-table class="unassigned-table">
                    <sp-table-head>
                      <sp-table-row>
                        <sp-table-head-cell class="col-email">${msg('Email')}</sp-table-head-cell>
                        <sp-table-head-cell>${msg('Status')}</sp-table-head-cell>
                        <sp-table-head-cell>${msg('Organization')}</sp-table-head-cell>
                        <sp-table-head-cell>${msg('Actions')}</sp-table-head-cell>
                      </sp-table-row>
                    </sp-table-head>
                    <sp-table-body>
                      ${this.unassignedUsers.map((user) =>
                        this.renderUnassignedUserRow(user)
                      )}
                    </sp-table-body>
                  </sp-table>
                </div>
              `
        }
      </section>
    `;
  }

  private renderUnassignedUserRow(user: AdminUser) {
    const canAssign = this.canAssignUser(user);
    const selectedOrgId =
      this.selectedOrgByUser[user.id] || this.orgs[0]?.id || '';
    return html`
      <sp-table-row>
        <sp-table-cell class="col-email">
          ${this.renderUserEmail(user)}
        </sp-table-cell>
        <sp-table-cell>${this.renderUserStatus(user)}</sp-table-cell>
        <sp-table-cell>
          <sp-picker
            size="s"
            .value=${selectedOrgId}
            ?disabled=${!canAssign || this.orgs.length === 0}
            @change=${(event: Event) => this.selectOrgForUser(user.id, event)}
          >
            ${this.orgs.map(
              (org) =>
                html`<sp-menu-item value=${org.id}>${org.name}</sp-menu-item>`
            )}
          </sp-picker>
        </sp-table-cell>
        <sp-table-cell>
          <sp-button
            size="s"
            variant="secondary"
            ?disabled=${
              !canAssign ||
              this.orgs.length === 0 ||
              this.assigningUserId === user.id
            }
            @click=${() => this.assignUserToSelectedOrg(user.id)}
          >
            ${msg('Assign')}
          </sp-button>
        </sp-table-cell>
      </sp-table-row>
    `;
  }

  private renderOrganizationCard(org: AdminOrganizationSummary) {
    const orgUsers = this.getUsersForOrg(org.id);
    const counts = this.getCountsForOrg(org.id);
    return html`
      <section class="org-card">
        <div class="org-card-header">
          <div class="org-copy">
            <h2 class="org-title">${org.name}</h2>
            ${
              org.description
                ? html`<p class="org-description">${org.description}</p>`
                : nothing
            }
          </div>
          <div class="org-meta">
            <span class="status-pill success">${msg(str`${counts.activeCount} active`)}</span>
            <span class="status-pill neutral">${msg(str`${counts.totalCount} total`)}</span>
          </div>
        </div>
        <div class="org-card-body">
          <div class="feature-bulk-actions">
            ${this.featureFlags.map((flag) =>
              this.renderOrgFeatureAction(org.id, flag)
            )}
          </div>
          ${
            orgUsers.length === 0
              ? html`<div class="state-panel">${msg('No users in this organization.')}</div>`
              : this.renderOrganizationUsersTable(org.id, orgUsers)
          }
        </div>
      </section>
    `;
  }

  private renderOrgFeatureAction(orgId: string, flag: FeatureFlagDefinition) {
    return html`
      <div class="feature-action-row">
        <span class="feature-action-label">
          ${this.formatFeatureFlagLabel(flag)}
        </span>
        <div class="feature-action-buttons">
          <sp-button
            size="s"
            variant="secondary"
            @click=${() => this.toggleOrgFeature(orgId, flag.key, true)}
          >
            ${msg('Enable for all')}
          </sp-button>
          <sp-button
            size="s"
            variant="secondary"
            @click=${() => this.requestDisableOrgFeature(orgId, flag.key)}
          >
            ${msg('Disable for all')}
          </sp-button>
        </div>
      </div>
    `;
  }

  private renderOrganizationUsersTable(
    orgId: string,
    orgUsers: Array<{ user: AdminUser; removed: boolean }>
  ) {
    return html`
      <div class="table-shell">
        <sp-table class="members-table">
          <sp-table-head>
            <sp-table-row>
              <sp-table-head-cell class="col-email">${msg('Email')}</sp-table-head-cell>
              <sp-table-head-cell>${msg('Status')}</sp-table-head-cell>
              ${this.featureFlags.map(
                (flag) =>
                  html`<sp-table-head-cell>
                    ${this.formatFeatureFlagLabel(flag)}
                  </sp-table-head-cell>`
              )}
              <sp-table-head-cell>${msg('Actions')}</sp-table-head-cell>
            </sp-table-row>
          </sp-table-head>
          <sp-table-body>
            ${orgUsers.map(({ user, removed }) =>
              this.renderOrganizationUserRow(orgId, user, removed)
            )}
          </sp-table-body>
        </sp-table>
      </div>
    `;
  }

  private renderOrganizationUserRow(
    orgId: string,
    user: AdminUser,
    removed: boolean
  ) {
    return html`
      <sp-table-row class=${removed ? 'user-removed' : ''}>
        <sp-table-cell class="col-email">
          ${this.renderUserEmail(user, removed)}
        </sp-table-cell>
        <sp-table-cell>${this.renderUserStatus(user)}</sp-table-cell>
        ${
          removed
            ? this.featureFlags.map(
                () => html`<sp-table-cell class="cell-muted">—</sp-table-cell>`
              )
            : this.featureFlags.map((flag) =>
                this.renderUserFeatureToggle(user, flag)
              )
        }
        <sp-table-cell>
          ${
            removed
              ? html`
                  <sp-button
                    size="s"
                    variant="secondary"
                    ?disabled=${this.restoringUserId === user.id}
                    @click=${() => this.restoreUserToOrg(orgId, user.id)}
                  >
                    ${msg('Restore')}
                  </sp-button>
                `
              : html`
                  <sp-button
                    size="s"
                    variant="secondary"
                    class="admin-danger-button"
                    ?disabled=${this.removingUserId === user.id}
                    @click=${() => this.requestRemoveUserFromOrg(orgId, user.id)}
                  >
                    ${msg('Remove')}
                  </sp-button>
                `
          }
        </sp-table-cell>
      </sp-table-row>
    `;
  }

  private renderUserFeatureToggle(
    user: AdminUser,
    flag: FeatureFlagDefinition
  ) {
    const enabled = this.getUserFlagValue(user.id, flag.key);
    return html`
      <sp-table-cell>
        <sp-button
          size="s"
          variant=${enabled ? 'accent' : 'secondary'}
          class=${enabled ? 'flag-toggle-enabled' : 'flag-toggle-disabled'}
          ?disabled=${this.loading && this.updatingUserId === user.id}
          @click=${() =>
            enabled
              ? this.requestDisableUserFlag(user.id, flag.key)
              : this.toggleUserFlag(user.id, flag.key, true)}
        >
          ${enabled ? msg('Enabled') : msg('Disabled')}
        </sp-button>
      </sp-table-cell>
    `;
  }

  render() {
    if (this.loading && !this.metadata) {
      return this.renderPage(html`
        <div class="state-panel">${msg('Loading admin data...')}</div>
      `);
    }

    if (!this.metadata) {
      return this.renderPage(html`
        <div class="state-panel">${msg('No admin data available.')}</div>
        ${this.errorMessage ? html`<p class="error">${this.errorMessage}</p>` : nothing}
      `);
    }

    return this.renderPage(html`
      <div class="admin-top-grid">
        ${this.renderCreateOrganizationPanel()}
        ${this.renderUnassignedUsersPanel()}
      </div>

      <div class="org-list">
        ${this.orgs.map((org) => this.renderOrganizationCard(org))}
      </div>

      ${this.errorMessage ? html`<p class="error">${this.errorMessage}</p>` : nothing}
    `);
  }
}

function inputValue(event: Event): string {
  return (event.currentTarget as { value?: string }).value ?? '';
}

declare global {
  interface HTMLElementTagNameMap {
    'kv-module-admin': KvModuleAdmin;
  }
}
